/* =========================================================
   VFW Vote Gallery — thumbnail carousel element (2026-09-17)
   Enhances a native scroll-snap track: arrows, thumbnails,
   arrow keys, active state, and the "Vote for the …" handoff
   to <vfw-vote-form> (vfw:vote-select). Never autoplays.
   Reduced motion → instant moves. No-JS: the track still
   swipes, thumbnails are anchor links, vote links jump to the
   form.
   ========================================================= */

(() => {
  if (customElements.get('vfw-vote-gallery')) return;

  class VfwVoteGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-vg-track]');
      this.slides = Array.from(this.querySelectorAll('[data-vg-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-vg-thumb]'));
      this.thumbStrip = this.querySelector('[data-vg-thumbs]');
      this.prev = this.querySelector('[data-vg-prev]');
      this.next = this.querySelector('[data-vg-next]');
      this.live = this.querySelector('[data-vg-live]');
      if (!this.track || !this.slides.length) return;

      this.index = 0;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

      const nav = this.querySelector('[data-vg-nav]');
      if (nav) nav.hidden = false;

      if (this.prev) this.prev.addEventListener('click', () => this.go(this.index - 1));
      if (this.next) this.next.addEventListener('click', () => this.go(this.index + 1));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.go(Number(thumb.dataset.vgThumb));
        });
      });

      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.go(this.index + 1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.go(this.index - 1);
        }
      });

      let ticking = false;
      this.track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            const w = this.track.clientWidth || 1;
            const i = Math.round(this.track.scrollLeft / w);
            if (i !== this.index) this.setActive(i, true);
          });
        },
        { passive: true }
      );

      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-vg-vote]');
        if (!link || !document.querySelector('vfw-vote-form')) return;
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('vfw:vote-select', { detail: { handle: link.dataset.vgVote } }));
      });

      this.setActive(0, false);
    }

    go(i) {
      const target = Math.max(0, Math.min(this.slides.length - 1, i));
      this.track.scrollTo({
        left: target * this.track.clientWidth,
        behavior: this.reduce.matches ? 'auto' : 'smooth',
      });
      this.setActive(target, true);
    }

    setActive(i, announce) {
      this.index = Math.max(0, Math.min(this.slides.length - 1, i));

      this.thumbs.forEach((thumb, n) => {
        const on = n === this.index;
        thumb.classList.toggle('is-active', on);
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.slides.length - 1;

      // Keep the active thumbnail in view by moving the strip only —
      // scrollIntoView would also scroll the page.
      const active = this.thumbs[this.index];
      if (active && this.thumbStrip) {
        const item = active.parentElement;
        const left = item.offsetLeft - (this.thumbStrip.clientWidth - item.offsetWidth) / 2;
        this.thumbStrip.scrollTo({ left: Math.max(0, left), behavior: this.reduce.matches ? 'auto' : 'smooth' });
      }

      if (announce && this.live) {
        this.live.textContent = this.slides[this.index].getAttribute('aria-label') || '';
      }
    }
  }

  customElements.define('vfw-vote-gallery', VfwVoteGallery);
})();
